/**
 * lp-editor.tsx
 * ─────────────
 * Shared in-canvas content-editing primitives for the LP Express renderer.
 *
 * The LP Express live preview doubles as an in-canvas editor. Section-level
 * move/hide/delete is handled by the renderer's hover toolbar; this file adds
 * the next layer — inline text editing, per-item hide/delete, and per-image
 * regenerate — and threads the handlers down to deeply nested section
 * components via a React context (mirrors LpStatusDotsContext) so we avoid
 * prop-drilling through every block.
 *
 * When no editor is present (the published/public page) every primitive
 * degrades to read-only: EditableText renders plain text, EditableItem skips
 * hidden items, and the regenerate affordances never appear.
 */
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Eye, EyeOff, Trash2, RotateCw, Pencil, X, Check, RotateCcw,
  Type, Bold, AlignLeft, AlignCenter, AlignRight, Palette,
  ChevronUp, ChevronDown, Upload, Loader2, Images, Link2,
  MessageSquareQuote, Star,
} from "lucide-react";
import {
  LP_SPACING_SCALES,
  LP_COPY_BLOCK_LABELS,
  LP_THEME_FONTS,
  LP_FONT_KEYS,
  lpCopyBlockLabel,
  lpSectionCopyBlocks,
  lpSectionHasImages,
  lpBrandFontToken,
  resolveLpElementFontStack,
  type LpSectionType,
  type LpElementStyle,
  type LpTheme,
  type LpComponentOrder,
} from "@shared/lp-express-sections";
import { GOOGLE_FONT_CATALOG } from "@shared/google-fonts";
import { isUsableBrandFont } from "@shared/brand-fonts";
import { previewFamilyFor, ensureGoogleFontsLoaded } from "@/lib/brand-font-loader";
import { validateCheckoutUrl, validateLinkHref, type BrandFont } from "@shared/schema";

// ── In-page link targets ──────────────────────────────────────────────
// The renderer publishes the page's linkable sections (label + anchor id) so
// the link-URL popover can offer a "jump to a section" picker. Empty by default
// (e.g. on the public page, where no editor / popover is mounted).
export type LpLinkTarget = { anchorId: string; label: string };
const LpPageSectionsContext = createContext<ReadonlyArray<LpLinkTarget>>([]);
export function LpPageSectionsProvider({
  value,
  children,
}: {
  value: ReadonlyArray<LpLinkTarget>;
  children: React.ReactNode;
}) {
  return <LpPageSectionsContext.Provider value={value}>{children}</LpPageSectionsContext.Provider>;
}
export function useLpPageSections(): ReadonlyArray<LpLinkTarget> {
  return useContext(LpPageSectionsContext);
}

// ── Template version (Long Form v1 vs v2) ─────────────────────────────
// v2 ("long-form-v2") is the globally-available, brand-adaptable variant. The
// renderer wraps the section tree with the page's template version; every
// section reads this and, in v2 ONLY, swaps each hardcoded Papabear / skincare
// / location fallback for a neutral, brand/data/AI-driven one. v1 stays
// byte-identical (default false), so existing pages are untouched.
const LpTemplateVersionContext = createContext<boolean>(false);
export function LpTemplateVersionProvider({
  value,
  children,
}: {
  value: boolean;
  children: React.ReactNode;
}) {
  return <LpTemplateVersionContext.Provider value={value}>{children}</LpTemplateVersionContext.Provider>;
}
export function useLpV2(): boolean {
  return useContext(LpTemplateVersionContext);
}

// A full path into a page's generatedContent, e.g. ["hero","headline"] or
// ["faq","faqs",2,"answer"]. Item ops carry the path to the array + an index.
export type LpContentPath = (string | number)[];

/* ─────────────────────────────────────────────
   FLOATING PANEL
   Renders an editor popover (style controls, photo picker, …) into a body-level
   portal with fixed positioning anchored to a trigger element. This escapes the
   section's local stacking context so the panel always paints OVER later page
   content (images/sections) instead of being covered by it. Position is
   recomputed on open and on any scroll/resize so it tracks the anchor.
───────────────────────────────────────────── */
function FloatingPanel({
  anchorRef,
  open,
  placement = "bottom",
  align = "right",
  width,
  gap = 6,
  panelRef,
  className,
  onClick,
  children,
  testId,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  placement?: "top" | "bottom";
  align?: "left" | "right";
  width: number;
  gap?: number;
  panelRef?: React.Ref<HTMLDivElement>;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  testId?: string;
}) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    // Derive the document/window that owns the anchor so the panel positions and
    // listens within the correct frame. In the main app these resolve to the
    // global document/window; inside the mobile-preview iframe they resolve to
    // the iframe's, so popovers stay anchored to their trigger there too.
    const anchorWin = anchorRef.current?.ownerDocument?.defaultView ?? window;
    const compute = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const maxLeft = anchorWin.innerWidth - width - 8;
      const left =
        align === "right"
          ? Math.max(8, Math.min(r.right - width, maxLeft))
          : Math.max(8, Math.min(r.left, maxLeft));
      const s: React.CSSProperties = { position: "fixed", left, width, zIndex: 9999 };
      if (placement === "top") {
        s.bottom = Math.max(8, anchorWin.innerHeight - r.top + gap);
      } else {
        s.top = Math.min(r.bottom + gap, anchorWin.innerHeight - 8);
      }
      setStyle(s);
    };
    compute();
    anchorWin.addEventListener("scroll", compute, true);
    anchorWin.addEventListener("resize", compute);
    return () => {
      anchorWin.removeEventListener("scroll", compute, true);
      anchorWin.removeEventListener("resize", compute);
    };
  }, [open, placement, align, width, gap, anchorRef]);

  const portalDoc = anchorRef.current?.ownerDocument ?? (typeof document !== "undefined" ? document : null);
  if (!open || !style || !portalDoc) return null;
  return createPortal(
    <div ref={panelRef} style={style} className={className} onClick={onClick} data-testid={testId}>
      {children}
    </div>,
    portalDoc.body,
  );
}

export interface LpEditorControls {
  // ── Section-level layout (existing) ──
  onMove: (type: LpSectionType, dir: "up" | "down") => void;
  onToggleHidden: (type: LpSectionType) => void;
  onDelete: (type: LpSectionType) => void;
  /** True while a layout change is being persisted — disables the section
   *  toolbar to avoid overlapping saves. */
  busy?: boolean;

  // ── Content-level editing (new) ──
  /** Save a new text value at an exact generatedContent path. */
  onEditText?: (path: LpContentPath, value: string) => void;
  /** Save a new href at an exact generatedContent path pointing at a discrete
   *  {label,url} anchor field's `url` (e.g. proof.research_links[i].url). Empty
   *  is allowed (=> inert, non-clickable link); a non-empty value must be a
   *  well-formed http(s) URL (validated client- and server-side). */
  onEditLinkUrl?: (path: LpContentPath, url: string) => void;
  /** Set/clear the `hidden` flag on item `index` inside the array at `path`. */
  onItemSetHidden?: (path: LpContentPath, index: number, hidden: boolean) => void;
  /** Remove item `index` from the array at `path`. */
  onItemDelete?: (path: LpContentPath, index: number) => void;
  /** Move item `index` up/down by swapping it with its neighbor in the array at
   *  `path`. Reorders the actual data, so the new order shows on the published
   *  page too. */
  onItemMove?: (path: LpContentPath, index: number, dir: "up" | "down") => void;

  // ── Per-page video URL overrides (new) ──
  /** Set (non-empty) or clear (empty/null) the per-page override URL for one of
   *  the two Farmstand video slots. Stored on the page's generatedContent — it
   *  never touches the brand record. */
  onSetVideoUrl?: (slot: "making" | "lifestyle" | "coffee-shop-reel", url: string | null) => void;

  // ── Element-level hide/delete (new) ──
  /** Set/clear the `hidden` flag for a single element keyed by a stable string
   *  (a heading, subtext block, image, or video). Hidden = dimmed in the editor,
   *  omitted on the published page. */
  onSetFieldHidden?: (key: string, hidden: boolean) => void;
  /** Mark a single element (keyed by a stable string) as deleted — removed from
   *  both the editor and the published page. */
  onDeleteField?: (key: string) => void;

  // ── Component reorder (within a container) ──
  /** Persist a new ordering for one reorder group. `orderedKeys` is the COMPLETE
   *  ordered list of the group's member element keys (current display order).
   *  Scoped to a single container — never reorders across the whole page. */
  onMoveElement?: (groupKey: string, orderedKeys: string[]) => void;

  // ── WYSIWYG styling (new) ──
  /** Merge a partial theme patch into the page's global theme. A field set to
   *  null/"" clears it (back to the AI default). */
  onSetTheme?: (patch: Partial<LpTheme>) => void;
  /** Clear the whole global theme (back to AI defaults). */
  onResetTheme?: () => void;
  /** Merge a partial style patch for one element/section keyed by a stable
   *  string (`f:…` / explicit / `sec:…`). null/"" clears that field. */
  onSetElementStyle?: (key: string, patch: Partial<LpElementStyle>) => void;
  /** Clear all style overrides for one element/section. */
  onResetElementStyle?: (key: string) => void;

  // ── Per-page media uploads (new) ──
  /** Upload a user-provided file to replace a single media slot. For images,
   *  `key` is the stable slot key (e.g. "hero:0"); the durable URL is stored on
   *  generatedContent.mediaOverrides, SHADOWING the AI slot. For videos, `slot`
   *  is one of the Farmstand video slots and the URL is stored on
   *  generatedContent.videoOverrides. Rejects on a wrong-type/oversized file or
   *  any upload error, leaving existing media untouched. */
  onUploadMedia?: (
    target: { kind: "image"; key: string } | { kind: "video"; slot: "making" | "lifestyle" | "coffee-shop-reel" },
    file: File,
  ) => Promise<void>;
  /** Revert one image slot back to its AI-generated image by clearing its
   *  media override (keyed by the same slot key used for onUploadMedia). */
  onClearMedia?: (key: string) => void;
  /** Point one image slot at an already-existing URL (e.g. one of the product's
   *  gallery photos) without uploading a file. Stored on
   *  generatedContent.mediaOverrides, keyed by the same slot key. */
  onChooseMedia?: (key: string, url: string) => void;

  // ── Per-page review override (new) ──
  /** Feature a specific real product review at a slot (e.g. the hero buy-box).
   *  A SNAPSHOT of the review is stored on generatedContent.reviewOverrides,
   *  keyed by a stable slot key, SHADOWING the default first-review pick. */
  onChooseReview?: (key: string, review: LpReviewChoice) => void;
  /** Revert one review slot back to the default first-review pick by clearing
   *  its review override (keyed by the same slot key). */
  onClearReview?: (key: string) => void;

  // ── Regenerate copy with feedback (new) ──
  /** Re-run a single copy block's workflow with optional free-text `feedback`
   *  and the block's current copy (threaded server-side into the Sim payload). */
  onRegenerateBlock?: (blockKey: string, feedback: string) => void;
  /** Re-run a section with the shared optional `feedback`. Scope "text"
   *  regenerates copy only; "images" re-renders the section's image slots only;
   *  "both" does both. */
  onRegenerateSection?: (
    type: LpSectionType,
    feedback: string,
    scope: "text" | "images" | "both",
  ) => void;
  /** True while a content change is being persisted — disables item/regenerate
   *  controls to avoid overlapping saves. */
  contentBusy?: boolean;
  /** True while the named copy block's regeneration workflow is in flight. Drives
   *  the per-block / per-section "Regenerating…" spinners and dims the affected
   *  text while the new copy is being generated. */
  isBlockRegenerating?: (blockKey: string) => boolean;
}

const LpEditorContext = createContext<LpEditorControls | null>(null);

/* ─────────────────────────────────────────────
   ELEMENT STATE (hide / delete)
   A map of stable element key → { hidden, deleted }, provided by the renderer
   from generatedContent.elements on BOTH the editor and the published page (so
   hidden/deleted elements are honored read-only too). Separate from the editor
   controls context because the public page has no editor but still needs the
   state to omit hidden/deleted elements.
───────────────────────────────────────────── */
export type LpElementState = Record<string, { hidden?: boolean; deleted?: boolean } | undefined>;

const LpElementStateContext = createContext<LpElementState>({});

export function LpElementStateProvider({
  value,
  children,
}: {
  value: LpElementState | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpElementStateContext.Provider value={value ?? {}}>{children}</LpElementStateContext.Provider>
  );
}

/** Read the hidden/deleted state for one element key. Safe to call with an empty
 *  key (returns the all-false default). */
export function useLpElementState(key?: string): { hidden: boolean; deleted: boolean } {
  const map = useContext(LpElementStateContext);
  const st = key ? map[key] : undefined;
  return { hidden: !!st?.hidden, deleted: !!st?.deleted };
}

/* ─────────────────────────────────────────────
   MEDIA OVERRIDES (user-uploaded images)
   A map of stable slot key → { url } for a user-uploaded image that SHADOWS the
   AI-generated slot at render time. Provided by the renderer from
   generatedContent.mediaOverrides on BOTH the editor and the published page (so
   an upload is honored read-only too). The underlying AI slot is left intact so
   its regeneration context survives — reverting clears the override.
───────────────────────────────────────────── */
export type LpMediaOverrideState = Record<string, { url?: string } | undefined>;

const LpMediaOverrideContext = createContext<LpMediaOverrideState>({});

export function LpMediaOverrideProvider({
  value,
  children,
}: {
  value: LpMediaOverrideState | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpMediaOverrideContext.Provider value={value ?? {}}>{children}</LpMediaOverrideContext.Provider>
  );
}

/** Read the uploaded override URL for one slot key (or undefined when none).
 *  Safe to call with an empty key. */
export function useLpMediaOverride(key?: string): string | undefined {
  const map = useContext(LpMediaOverrideContext);
  const v = key ? map[key] : undefined;
  const url = v?.url;
  return typeof url === "string" && url.trim() ? url : undefined;
}

/** Read the whole override map (for callers that resolve several keys at once,
 *  e.g. the hero carousel). */
export function useLpMediaOverridesMap(): LpMediaOverrideState {
  return useContext(LpMediaOverrideContext);
}

/* ─────────────────────────────────────────────
   REVIEW OVERRIDES (user-picked featured review)
   A map of stable slot key (e.g. "hero") → a snapshot of the real product
   review the user chose to feature there, SHADOWING the default first-review
   pick at render time. Provided by the renderer from
   generatedContent.reviewOverrides on BOTH the editor and the published page (so
   the picked review is honored read-only too). Clearing reverts to the default.
───────────────────────────────────────────── */
export type LpReviewChoice = { reviewer: string | null; reviewText: string; rating: number };
export type LpReviewOverrideState = Record<string, LpReviewChoice | undefined>;

const LpReviewOverrideContext = createContext<LpReviewOverrideState>({});

export function LpReviewOverrideProvider({
  value,
  children,
}: {
  value: LpReviewOverrideState | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpReviewOverrideContext.Provider value={value ?? {}}>{children}</LpReviewOverrideContext.Provider>
  );
}

/** Read the picked review for one slot key (or undefined when none). Safe to
 *  call with an empty key. */
export function useLpReviewOverride(key?: string): LpReviewChoice | undefined {
  const map = useContext(LpReviewOverrideContext);
  const v = key ? map[key] : undefined;
  return v && typeof v.reviewText === "string" && v.reviewText.trim() ? v : undefined;
}

/* ─────────────────────────────────────────────
   COMPONENT ORDER (within a container)
   A map of stable group key → ordered element keys, provided by the renderer
   from generatedContent.componentOrder on BOTH the editor and the published page
   (so a persisted order is honored read-only too). Reordering is SCOPED to a
   single container — a component only shuffles relative to its sibling cards,
   never across the whole page (sections own cross-page order).
───────────────────────────────────────────── */
const LpComponentOrderContext = createContext<LpComponentOrder>({});

export function LpComponentOrderProvider({
  value,
  children,
}: {
  value: LpComponentOrder | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpComponentOrderContext.Provider value={value ?? {}}>
      {children}
    </LpComponentOrderContext.Provider>
  );
}

/** Read the persisted ordering for one group key (or undefined when none set). */
export function useLpComponentOrder(groupKey?: string): string[] | undefined {
  const map = useContext(LpComponentOrderContext);
  return groupKey ? map[groupKey] : undefined;
}

/* The reorder group a child EditableElement belongs to. Provided by
   <LpReorderGroup>; lets each member compute its position (first/last) and emit
   the complete new order when its up/down arrow is clicked. */
const LpReorderGroupContext = createContext<{ groupKey: string; orderedKeys: string[] } | null>(
  null,
);

/** Reorder canonical-order `items` by a persisted `order` array. Keys present in
 *  `order` come first (in that order); any item not mentioned (a newly added
 *  canonical member) is appended in its canonical position; order keys that no
 *  longer exist are ignored. Stale-proof — never drops or duplicates an item. */
function orderReorderItems<T extends { key: string }>(items: T[], order?: string[]): T[] {
  if (!order || order.length === 0) return items;
  const byKey = new Map(items.map((it) => [it.key, it] as const));
  const out: T[] = [];
  for (const k of order) {
    const it = byKey.get(k);
    if (it) {
      out.push(it);
      byKey.delete(k);
    }
  }
  for (const it of items) if (byKey.has(it.key)) out.push(it);
  return out;
}

/**
 * Render a stack of sibling components in the page's persisted order (falling
 * back to canonical author order when none is set). Each item carries a stable
 * `key` matching the element key its <EditableElement> uses for hide/delete, so
 * a member can render up/down arrows that reorder ONLY within this container.
 */
export function LpReorderGroup({
  groupKey,
  items,
}: {
  groupKey: string;
  items: { key: string; node: React.ReactNode }[];
}) {
  const order = useLpComponentOrder(groupKey);
  const ordered = orderReorderItems(items, order);
  const orderedKeys = ordered.map((it) => it.key);
  return (
    <LpReorderGroupContext.Provider value={{ groupKey, orderedKeys }}>
      {ordered.map((it) =>
        // Attach the key to the item's own root element rather than a
        // React.Fragment: the dev cartographer plugin injects a
        // `data-replit-metadata` prop onto JSX nodes, and a Fragment rejects
        // any prop other than `key`/`children` (console warning).
        React.isValidElement(it.node) ? (
          React.cloneElement(it.node as React.ReactElement, { key: it.key })
        ) : (
          <React.Fragment key={it.key}>{it.node}</React.Fragment>
        ),
      )}
    </LpReorderGroupContext.Provider>
  );
}

/* ─────────────────────────────────────────────
   PER-ELEMENT / SECTION STYLE
   A map of stable element key → LpElementStyle, provided by the renderer from
   generatedContent.styles on BOTH the editor and the published page (so the
   formatting survives read-only). Mirrors LpElementStateContext.
───────────────────────────────────────────── */
export type LpStyleMap = Record<string, LpElementStyle | undefined>;

const LpElementStyleContext = createContext<LpStyleMap>({});

export function LpElementStyleProvider({
  value,
  children,
}: {
  value: LpStyleMap | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpElementStyleContext.Provider value={value ?? {}}>{children}</LpElementStyleContext.Provider>
  );
}

/** Read the per-element style override for one key (or undefined). */
export function useLpElementStyle(key?: string): LpElementStyle | undefined {
  const map = useContext(LpElementStyleContext);
  return key ? map[key] : undefined;
}

/* ─────────────────────────────────────────────
   BRAND FONTS
   The brand's saved font list, provided by the renderer on BOTH the editor and
   the published page. Needed to (a) resolve a per-block "brand:<id>" fontFamily
   override to a concrete CSS stack, and (b) offer the brand's own fonts in the
   text-block Font picker. Empty by default (public page with no brand fonts).
───────────────────────────────────────────── */
const LpBrandFontsContext = createContext<BrandFont[]>([]);

export function LpBrandFontsProvider({
  value,
  children,
}: {
  value: BrandFont[] | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <LpBrandFontsContext.Provider value={value ?? []}>{children}</LpBrandFontsContext.Provider>
  );
}

/** Read the brand's saved fonts (empty array when none). */
export function useLpBrandFonts(): BrandFont[] {
  return useContext(LpBrandFontsContext);
}

/** Convert a per-element style to the inline CSS props that apply to TEXT
 *  elements (size/weight/align/color). Inline style wins over Tailwind classes,
 *  so this is how an override visibly takes effect. Returns undefined when there
 *  is nothing to apply (so we never spread an empty object onto every element). */
export function lpTextStyle(style: LpElementStyle | undefined): React.CSSProperties | undefined {
  if (!style) return undefined;
  const out: React.CSSProperties = {};
  if (typeof style.fontSize === "number") out.fontSize = `${style.fontSize}px`;
  if (style.fontWeight) out.fontWeight = style.fontWeight === "bold" ? 700 : 400;
  if (style.align) out.textAlign = style.align;
  if (style.color) out.color = style.color;
  return Object.keys(out).length > 0 ? out : undefined;
}

const LP_SECTION_PAD: Record<string, string> = {
  compact: "1.75rem",
  default: "",
  spacious: "5.5rem",
};

/** Convert a per-section style to inline CSS props (background tone + vertical
 *  padding). Used by the renderer's section wrapper on editor + published. */
export function lpSectionStyle(style: LpElementStyle | undefined): React.CSSProperties | undefined {
  if (!style) return undefined;
  const out: React.CSSProperties = {};
  if (style.background) out.backgroundColor = style.background;
  if (style.paddingY && LP_SECTION_PAD[style.paddingY]) {
    out.paddingTop = LP_SECTION_PAD[style.paddingY];
    out.paddingBottom = LP_SECTION_PAD[style.paddingY];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/* ─────────────────────────────────────────────
   GLOBAL THEME
   The page's single global theme object, provided by the renderer on BOTH the
   editor and the published page. The renderer turns it into root CSS vars; the
   Theme panel reads it back to show the current selection.
───────────────────────────────────────────── */
const LpThemeContext = createContext<LpTheme>({});

export function LpThemeProvider({
  value,
  children,
}: {
  value: LpTheme | null | undefined;
  children: React.ReactNode;
}) {
  return <LpThemeContext.Provider value={value ?? {}}>{children}</LpThemeContext.Provider>;
}

/** Read the active global theme (empty object when none set). */
export function useLpTheme(): LpTheme {
  return useContext(LpThemeContext);
}

/** Ephemeral hover/active state: the copy block a Regenerate control is pointing
 *  at, so EVERY field that block rewrites can outline itself at once. This makes
 *  the otherwise-invisible scope of a regenerate ("what all does this touch?")
 *  visible the moment the user hovers or opens the control.
 *
 *  `section` scopes the outline to the control's own visual <section>. Some copy
 *  blocks (villain, quotes) are deliberately reused across several sections, so
 *  matching on block key alone would light up far-away sections; scoping to the
 *  local section keeps the highlight to the text right where the user clicked. */
const LpRegenHighlightContext = createContext<{
  block: string | null;
  section: HTMLElement | null;
  setBlock: (block: string | null, section?: HTMLElement | null) => void;
}>({ block: null, section: null, setBlock: () => {} });

export function useLpRegenHighlight() {
  return useContext(LpRegenHighlightContext);
}

export function LpEditorProvider({
  value,
  children,
}: {
  value: LpEditorControls | null | undefined;
  children: React.ReactNode;
}) {
  const [highlight, setHighlight] = useState<{
    block: string | null;
    section: HTMLElement | null;
  }>({ block: null, section: null });
  const setBlock = useRef(
    (block: string | null, section: HTMLElement | null = null) =>
      setHighlight({ block, section: block ? section : null }),
  ).current;
  return (
    <LpEditorContext.Provider value={value ?? null}>
      <LpRegenHighlightContext.Provider
        value={{ block: highlight.block, section: highlight.section, setBlock }}
      >
        {children}
      </LpRegenHighlightContext.Provider>
    </LpEditorContext.Provider>
  );
}

/** Read the active in-canvas editor controls, or null on the public page. */
export function useLpEditor(): LpEditorControls | null {
  return useContext(LpEditorContext);
}

/* ─────────────────────────────────────────────
   EDITABLE TEXT
   Click-to-edit in place. Save on blur/Enter, cancel on Escape. Uses
   contentEditable on the SAME styled element so the inline edit preserves the
   field's exact typography (no layout shift). On the public page it renders
   plain (optionally bold-formatted) text with no affordances.
───────────────────────────────────────────── */
/** When true, EditableText fields rendered beneath an <EditableElement> drop
 *  their own per-field hide/delete (the enclosing component owns it) while still
 *  surfacing the formatting popover. Provided by EditableElement so authors get
 *  component-level hide/delete for the whole card without having to flag every
 *  child field individually. */
const LpComponentFormatOnlyContext = createContext(false);

// A copy block's inline "Regenerate" control auto-anchors on the block's
// top-level heading field — identified by the SECOND path segment naming a
// canonical heading (e.g. ["proof","section_headline"], ["alternatives",
// "hero_headline"], ["villain","checklist_headline"]). Blocks whose heading
// doesn't follow this naming (hero, lead-in, advisory, quotes, showcase) opt in
// explicitly via the EditableText `regenAnchor` prop instead. "headline" is
// deliberately NOT listed: the hero renders its headline in multiple responsive
// copies, so it anchors explicitly to avoid duplicate controls.
const LP_REGEN_ANCHOR_HEADING_KEYS = new Set<string>([
  "section_headline",
  "hero_headline",
  "checklist_headline",
]);

export function EditableText({
  value,
  path,
  as = "span",
  className,
  style,
  placeholder,
  multiline = false,
  render,
  testId,
  elementKey,
  noHideDelete = false,
  formatOnly: formatOnlyProp = false,
  regenBlockKey,
  regenAnchor = false,
}: {
  value: string | null | undefined;
  path: LpContentPath;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  /** Allow Shift+Enter to insert a newline (Enter alone still saves). */
  multiline?: boolean;
  /** Optional formatter for display mode (e.g. markdown-bold). Editing always
   *  operates on the raw `value` string. */
  render?: (v: string) => React.ReactNode;
  testId?: string;
  /** Explicit stable key for this field's hide/delete state. When omitted, a key
   *  is auto-derived from `path` (so EVERY editable text is individually
   *  hide/delete-able by default). Two fields sharing the same `path` (e.g.
   *  responsive mobile/desktop duplicates) share one key on purpose. */
  elementKey?: string;
  /** Opt OUT of hide/delete (leave edit-only). Required when this text is
   *  rendered inside an interactive element (`<a>`/`<button>`), because the
   *  hide/delete toolbar contains buttons and nesting a button inside a button
   *  or anchor is invalid DOM. */
  noHideDelete?: boolean;
  /** Keep the per-element formatting popover (size/weight/align/color) but drop
   *  this field's individual hide/delete — used when the field lives inside a
   *  component-level <EditableElement> that owns hide/delete for the whole
   *  block, so the component is the single hide/delete unit. */
  formatOnly?: boolean;
  /** Explicit regenerate-copy block key for this field's inline "Regenerate"
   *  control. When omitted, the block key is auto-derived from the content
   *  path's root segment (e.g. ["showcase","headline"] → "showcase"); only
   *  roots present in LP_COPY_BLOCK_LABELS surface a control. Pass this when the
   *  path root doesn't match the workflow key (e.g. the hero buy-box lead-in,
   *  whose path is ["hero","buyBox","leadin"] but whose block key is "leadin"). */
  regenBlockKey?: string;
  /** Mark this field as the SINGLE place its copy block's inline "Regenerate"
   *  control appears. A copy block usually generates many fields at once (a
   *  recipe-card's title/ingredients/steps, an FAQ's questions/answers); we show
   *  one control at the block's top-level field (its heading) rather than on
   *  every generated field. The control auto-anchors on canonical heading paths
   *  (LP_REGEN_ANCHOR_HEADING_KEYS); pass this to anchor a block whose heading
   *  doesn't follow that naming (e.g. hero, lead-in, advisory, quotes). Every
   *  field in the block still dims while regenerating regardless of anchoring. */
  regenAnchor?: boolean;
}) {
  const editor = useLpEditor();
  const regenHighlight = useLpRegenHighlight();
  // An enclosing <EditableElement> makes the whole component the single
  // hide/delete unit, so any field inside it is implicitly formatOnly.
  const inheritedFormatOnly = useContext(LpComponentFormatOnlyContext);
  const formatOnly = formatOnlyProp || inheritedFormatOnly;
  // Auto-derive a per-field key from the content path when none is given. The
  // `f:` namespace keeps derived keys from ever colliding with explicit keys.
  const effectiveKey = noHideDelete
    ? undefined
    : elementKey ?? `f:${(path as ReadonlyArray<string | number>).join(".")}`;
  const elState = useLpElementState(effectiveKey);
  const elStyle = useLpElementStyle(effectiveKey);
  const brandFonts = useLpBrandFonts();
  const textStyle = lpTextStyle(elStyle);
  // Resolve a per-block fontFamily override (curated key | Google family |
  // "brand:<id>" token) to a concrete CSS stack. Brand tokens need the brand's
  // saved fonts to resolve; an unresolvable/absent override yields undefined so
  // the field keeps its Tailwind typography (byte-identical to today).
  const fontStack = resolveLpElementFontStack(elStyle?.fontFamily, brandFonts);
  // Per-element inline overrides win over the field's Tailwind typography. Merged
  // into every branch (read-only, editing, display) so the formatting is visible
  // both in the canvas and on the published page.
  const mergedStyle: React.CSSProperties | undefined =
    textStyle || fontStack
      ? { ...style, ...textStyle, ...(fontStack ? { fontFamily: fontStack } : {}) }
      : style;
  const ref = useRef<HTMLElement>(null);
  // The rendered (display) element, used to test whether this field falls inside
  // the section a Regenerate control is pointing at. `ref` only attaches to the
  // edit box, so the display branches need their own handle for the scope check.
  const viewRef = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const cancelRef = useRef(false);
  // The exact text the edit box was seeded with, so an unchanged blur never
  // persists (esp. when we seed a placeholder over an empty value).
  const seededRef = useRef("");
  const editable = !!editor?.onEditText;
  const canHideDelete =
    !formatOnly &&
    !!effectiveKey &&
    !!editor &&
    !!(editor.onSetFieldHidden || editor.onDeleteField);
  // formatOnly fields still surface the formatting popover (via FieldToolbar)
  // even though they have no individual hide/delete.
  const canFormat = !!effectiveKey && !!editor && !!editor.onSetElementStyle;
  // Inline per-block "Regenerate" control: derive the copy-block key from an
  // explicit prop or the content-path root, but only when it names a known
  // regenerable copy workflow (LP_COPY_BLOCK_LABELS). Image/footer roots resolve
  // to undefined and surface no control. "hero" is excluded from path-root
  // auto-derivation because its subtree also holds buy-box pricing labels and
  // benefit pills that the hero copy workflow does NOT re-roll — the true hero
  // copy fields opt in explicitly via regenBlockKey="hero".
  const regenBlock =
    regenBlockKey ??
    (typeof path[0] === "string" &&
    (path[0] as string) !== "hero" &&
    (path[0] as string) in LP_COPY_BLOCK_LABELS
      ? (path[0] as string)
      : undefined);
  // A copy block usually generates MANY fields from one workflow (a recipe
  // card's title/ingredients/steps, an FAQ's questions/answers). Showing a
  // "Regenerate" control on every one of those fields is noise — they all
  // re-roll the same block. Surface it ONCE, on the block's top-level field:
  // either a canonical heading path (auto-anchor) or an explicit regenAnchor for
  // blocks whose heading doesn't follow that naming (hero, lead-in, advisory…).
  const isRegenAnchor =
    regenAnchor ||
    (typeof path[1] === "string" &&
      LP_REGEN_ANCHOR_HEADING_KEYS.has(path[1] as string));
  // The per-block control renders a real <button>; never surface it on a field
  // hosted inside an interactive element (<a>/<button>), which uses noHideDelete
  // — nesting a button there is invalid DOM.
  const canRegen =
    !noHideDelete &&
    !!regenBlock &&
    isRegenAnchor &&
    !!editor &&
    !!editor.onRegenerateBlock;
  const showToolbar = canHideDelete || (formatOnly && canFormat) || canRegen;
  // True while this field's copy block is being regenerated — dims the text and
  // shows an inline spinner so the user sees the new copy is on its way.
  const regenerating =
    !!regenBlock && !!editor?.isBlockRegenerating?.(regenBlock);

  // On entering edit mode, populate the element with the RAW value (display mode
  // may show a formatted version), focus it, and drop the caret at the end.
  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    const raw = value ?? "";
    // When the bound value is empty, the field still SHOWS its placeholder as
    // plain text (display = value || placeholder), so the user perceives the
    // placeholder as real content. Seed the edit box with that visible
    // placeholder instead of an empty box — otherwise clicking to edit appears
    // to "wipe" the text. `render`-decorated fields are seeded with the raw
    // value only (their formatter re-applies decoration, so seeding the rendered
    // text would double it up).
    const seed = raw !== "" || render ? raw : placeholder ?? "";
    seededRef.current = seed;
    // Non-destructive entry: only seed the text when the contentEditable is a
    // pure text leaf. If it (unexpectedly) still holds rendered child ELEMENTS
    // — e.g. formatted/nested DOM from a `render` prop that didn't fully unmount
    // — never blank them; leave the existing content so focusing can't wipe it.
    const hasElementChildren = Array.from(el.childNodes).some(
      (n) => n.nodeType === Node.ELEMENT_NODE,
    );
    if (!hasElementChildren && el.textContent !== seed) {
      el.textContent = seed;
    }
    el.focus();
    // Use the element's OWN document/window so the caret is placed correctly even
    // when this field is rendered inside the mobile-preview iframe.
    const doc = el.ownerDocument;
    const range = doc.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = doc.defaultView?.getSelection() ?? null;
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const display = render ? render(value ?? "") : (value || placeholder || "");

  // Deleted elements are gone everywhere (editor + published).
  if (elState.deleted) return null;

  if (!editable) {
    // Read-only / published page: a hidden element is simply omitted.
    if (elState.hidden) return null;
    return React.createElement(as, { className, style: mergedStyle, "data-testid": testId }, display);
  }

  const commit = () => {
    const el = ref.current;
    setEditing(false);
    if (!el) return;
    const next = (el.textContent ?? "").trim();
    // Compare against what we seeded the box with (which may be the placeholder
    // over an empty value). Blurring without typing leaves `next === seeded`, so
    // a mere click-to-edit never persists the placeholder as real content.
    if (next === seededRef.current.trim()) return;
    if (next !== (value ?? "").trim()) editor!.onEditText!(path, next);
  };

  if (editing) {
    return React.createElement(as, {
      // Distinct key from the display branches forces React to fully unmount this
      // contentEditable node on commit. While editing, the browser inserts text
      // nodes React never tracks; reusing the same DOM node for display mode would
      // make React append the display text ON TOP of the leftover edit text →
      // duplicated text. Remounting discards the dirtied node.
      key: "lp-edit",
      ref,
      className: `${className ?? ""} outline outline-2 outline-dashed outline-primary rounded-sm`,
      style: { ...mergedStyle, whiteSpace: multiline ? "pre-wrap" : undefined, cursor: "text" },
      contentEditable: true,
      suppressContentEditableWarning: true,
      "data-testid": testId,
      onBlur: () => {
        if (cancelRef.current) {
          cancelRef.current = false;
          setEditing(false);
        } else {
          commit();
        }
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !(multiline && e.shiftKey)) {
          e.preventDefault();
          ref.current?.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelRef.current = true;
          ref.current?.blur();
        }
      },
    });
  }

  // When a Regenerate control is hovered/open, outline every field its block
  // will rewrite (including no-toolbar/noHideDelete fields) so the scope is
  // obvious. A sky ring — distinct from edit (primary) and regenerating (dark).
  // Scoped to the control's own <section>: shared blocks (villain/quotes) appear
  // in several sections, so we only light up the copy in the section the user is
  // pointing at, never far-away sections above/below.
  const blockHighlighted =
    !!regenBlock &&
    regenHighlight.block === regenBlock &&
    (!regenHighlight.section ||
      (!!viewRef.current && regenHighlight.section.contains(viewRef.current)));
  const baseCls = `${className ?? ""} cursor-text rounded-sm hover:outline hover:outline-1 hover:outline-dashed hover:outline-primary/60${blockHighlighted ? " outline outline-2 outline-sky-400 outline-offset-1" : ""}`;

  // If the consumer already establishes a positioning context (e.g. an
  // absolutely-centered eyebrow pill via `absolute left-1/2 -translate-x-1/2`),
  // do NOT override it with `relative` — that would break its layout. Any
  // non-static position still anchors the field toolbar correctly.
  const hasOwnPositioning =
    /(?:^|\s)(?:absolute|fixed|sticky)(?:\s|$)/.test(className ?? "") ||
    (!!style?.position && style.position !== "static");

  if (!showToolbar) {
    return React.createElement(
      as,
      {
        key: "lp-view",
        ref: viewRef,
        className: baseCls,
        style: mergedStyle,
        "data-testid": testId,
        title: "Click to edit",
        onClick: () => setEditing(true),
      },
      display,
    );
  }

  // formatOnly fields never carry their own hidden state (the enclosing
  // EditableElement owns it), so never dim/badge them here.
  const fieldHidden = !formatOnly && elState.hidden;

  // Editor + toolbar: render the element positioned (relative) so the tiny
  // field toolbar can sit on top, dim it when hidden, and append a small
  // "Hidden" badge. Toolbar children are span/button — valid inside any text
  // element (span/p/h*) and we never use this on a <button>.
  return React.createElement(
    as,
    {
      key: "lp-view",
      ref: viewRef,
      className: `${baseCls} ${hasOwnPositioning ? "" : "relative"} group/lpfield ${fieldHidden ? "opacity-40 grayscale" : ""} ${regenerating ? "pointer-events-none" : ""}`,
      style: hasOwnPositioning ? { ...mergedStyle } : { ...mergedStyle, position: "relative" },
      "data-testid": testId,
      title: "Click to edit",
      onClick: () => setEditing(true),
    },
    // Dim/pulse only the COPY while a regen is in flight — not the field element
    // itself — so the absolutely-positioned "Regenerating…" badge below stays at
    // full opacity (an opacity on the field would composite the badge down too).
    regenerating
      ? React.createElement("span", { key: "__lpfield_text", className: "opacity-50 animate-pulse" }, display)
      : display,
    React.createElement(FieldToolbar, {
      key: "__lpfield_toolbar",
      elementKey: effectiveKey,
      hidden: fieldHidden,
      formatOnly,
      // Only the block's anchor field surfaces the inline "Regenerate" control;
      // sibling fields in the same block still dim while it regenerates.
      regenBlockKey: canRegen ? regenBlock : undefined,
    }),
    fieldHidden
      ? React.createElement(HiddenBadge, { key: "__lpfield_badge" })
      : null,
    // Show the "Regenerating…" pill only on the anchor field, so a block in
    // flight surfaces one badge rather than one per generated field.
    regenerating && canRegen
      ? React.createElement(RegeneratingBadge, { key: "__lpfield_regen" })
      : null,
  );
}

/* ─────────────────────────────────────────────
   REGENERATING BADGE
   A tiny "Regenerating…" pill with a spinner, overlaid on a field while its copy
   block's workflow is in flight. Mirrors HiddenBadge's placement so it floats at
   the field's top-left without disturbing layout.
───────────────────────────────────────────── */
function RegeneratingBadge() {
  return React.createElement(
    "span",
    {
      contentEditable: false,
      className:
        "pointer-events-none absolute left-0 top-0 z-20 inline-flex items-center gap-1 rounded-br rounded-tl-sm bg-gray-900/85 px-1.5 py-0.5 font-sans text-[10px] font-semibold normal-case tracking-normal text-white shadow",
      "data-testid": "badge-regenerating",
    },
    React.createElement(Loader2, { key: "i", className: "h-3 w-3 animate-spin" }),
    "Regenerating…",
  );
}

/* ─────────────────────────────────────────────
   EDITABLE LINK
   A discrete {label, url} anchor (e.g. a Science/proof research link). On the
   published/read-only page it renders a plain anchor — inert (no href) when the
   url is empty. In the editor it never navigates (so label-edit clicks land),
   the label edits in place via EditableText (when a labelPath is given), and a
   sibling URL-edit popover edits the href. The popover button is a SIBLING of
   the anchor (never nested inside it) so we don't put a <button> inside an <a>.
───────────────────────────────────────────── */
export function EditableLink({
  path,
  url,
  label,
  labelPath,
  trailing,
  style,
  testId,
  fallbackLabel = "Source",
}: {
  /** Path to the anchor's `url` field, e.g. ["proof","research_links",0,"url"]. */
  path: LpContentPath;
  url: string;
  label: string;
  /** Path to the anchor's `label` field — when given, the label edits inline. */
  labelPath?: LpContentPath;
  /** Optional trailing node (e.g. an arrow icon) rendered after the label. */
  trailing?: React.ReactNode;
  style?: React.CSSProperties;
  testId?: string;
  fallbackLabel?: string;
}) {
  const editor = useLpEditor();

  // Read-only / published page: a plain anchor, inert when there's no url.
  if (!editor) {
    return (
      <a
        href={url || undefined}
        target={url ? "_blank" : undefined}
        rel={url ? "noopener noreferrer" : undefined}
        data-testid={testId}
        style={style}
      >
        <span>{label || fallbackLabel}</span>
        {trailing}
      </a>
    );
  }

  const labelNode =
    labelPath && editor.onEditText ? (
      <EditableText as="span" noHideDelete path={labelPath} value={label} placeholder={fallbackLabel} />
    ) : (
      <span>{label || fallbackLabel}</span>
    );

  return (
    <span className="group/lplink relative inline-flex">
      <a
        href={url || undefined}
        onClick={(e) => e.preventDefault()}
        data-testid={testId}
        style={style}
      >
        {labelNode}
        {trailing}
      </a>
      {editor.onEditLinkUrl && <LinkUrlPopover path={path} url={url} testId={testId} />}
    </span>
  );
}

/** The hover-revealed URL-edit affordance for an EditableLink. A small Link2
 *  button that opens a body-portaled popover (FloatingPanel) with a URL input.
 *  Empty saves an inert link; a non-empty value is validated http(s) before it
 *  persists (server re-validates). Sibling of the anchor — never nested in it. */
export function LinkUrlPopover({
  path,
  url,
  testId,
}: {
  path: LpContentPath;
  url: string;
  testId?: string;
}) {
  const editor = useLpEditor();
  const sectionTargets = useLpPageSections();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(url);
  const [err, setErr] = useState<string | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  if (!editor?.onEditLinkUrl) return null;

  // When the current value is a same-page anchor that matches a known section,
  // reflect it in the picker; otherwise the picker sits on "Custom URL…".
  const anchorVal =
    val.startsWith("#") && sectionTargets.some((t) => `#${t.anchorId}` === val)
      ? val.slice(1)
      : "";

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const toggle = (e: React.MouseEvent) => {
    stop(e);
    if (!open) {
      setVal(url);
      setErr(null);
    }
    setOpen((o) => !o);
  };

  const save = (e: React.MouseEvent) => {
    stop(e);
    const next = val.trim();
    const invalid = validateLinkHref(next);
    if (invalid) {
      setErr(invalid);
      return;
    }
    editor.onEditLinkUrl!(path, next);
    setOpen(false);
  };

  const keyBase = (path as ReadonlyArray<string | number>).join(".");

  return (
    <span
      ref={rootRef}
      contentEditable={false}
      className="absolute -top-2 -right-2 z-30 inline-flex"
    >
      <button
        type="button"
        className={`inline-flex h-5 w-5 items-center justify-center rounded-md border border-border bg-white/95 text-muted-foreground shadow-md opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/lplink:opacity-100 ${
          url ? "" : "text-primary opacity-100"
        }`}
        onClick={toggle}
        title={url ? "Edit link URL" : "Add link URL"}
        data-testid={testId ? `button-edit-link-${testId}` : `button-edit-link-${keyBase}`}
      >
        <Link2 className="h-3 w-3" />
      </button>
      <FloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        open={open}
        placement="bottom"
        align="right"
        width={264}
        onClick={stop}
        className="block cursor-default rounded-lg border border-border bg-white p-2.5 text-left shadow-xl"
        testId={`popover-link-${keyBase}`}
      >
        {sectionTargets.length > 0 && (
          <>
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Jump to a section
            </span>
            <select
              value={anchorVal}
              onClick={stop}
              onChange={(e) => {
                const id = e.target.value;
                setVal(id ? `#${id}` : "");
                if (err) setErr(null);
              }}
              className="mb-2 h-8 w-full rounded border border-border bg-white px-2 text-xs outline-none focus:border-primary"
              data-testid={`select-link-section-${keyBase}`}
            >
              <option value="">Custom URL…</option>
              {sectionTargets.map((t) => (
                <option key={t.anchorId} value={t.anchorId}>
                  {t.label}
                </option>
              ))}
            </select>
          </>
        )}
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Link URL
        </span>
        <input
          type="text"
          value={val}
          placeholder="https://example.com or #lp-faq"
          onClick={stop}
          onChange={(e) => {
            setVal(e.target.value);
            if (err) setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save(e as unknown as React.MouseEvent);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
          className="h-8 w-full rounded border border-border px-2 text-xs outline-none focus:border-primary"
          data-testid={`input-link-url-${keyBase}`}
        />
        {err ? (
          <span className="mt-1 block text-[11px] text-destructive" data-testid={`error-link-url-${keyBase}`}>
            {err}
          </span>
        ) : (
          <span className="mt-1 block text-[11px] text-muted-foreground">
            Leave empty for a non-clickable link.
          </span>
        )}
        <span className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              stop(e);
              setOpen(false);
            }}
            data-testid={`button-link-cancel-${keyBase}`}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90"
            onClick={save}
            data-testid={`button-link-save-${keyBase}`}
          >
            Save
          </button>
        </span>
      </FloatingPanel>
    </span>
  );
}

/* ─────────────────────────────────────────────
   FIELD TOOLBAR + HIDDEN BADGE
   Span-based (so they're valid children of inline text elements) hover controls
   for per-element hide/show + delete. Used by EditableText (elementKey) and
   EditableElement.
───────────────────────────────────────────── */
function FieldToolbar({
  elementKey,
  hidden,
  className,
  formatOnly = false,
  regenBlockKey,
}: {
  elementKey?: string;
  hidden?: boolean;
  className?: string;
  /** Render only the formatting popover (no hide/delete buttons) — used when an
   *  enclosing EditableElement owns hide/delete for the whole component. */
  formatOnly?: boolean;
  /** When set (and a regenerate handler is wired), surfaces an inline per-block
   *  "Regenerate" control with optional feedback for this field's copy block. */
  regenBlockKey?: string;
}) {
  const editor = useLpEditor();
  if (!editor) return null;
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <span
      contentEditable={false}
      className={`absolute -top-2 right-1 z-30 inline-flex items-center gap-0.5 rounded-md border border-border bg-white/95 p-0.5 shadow-md opacity-0 transition-opacity group-hover/lpfield:opacity-100 backdrop-blur-sm ${className ?? ""}`}
    >
      {regenBlockKey && editor.onRegenerateBlock && (
        <LpBlockRegenerate blockKey={regenBlockKey} />
      )}
      {elementKey && editor.onSetElementStyle && (
        <LpStylePopover elementKey={elementKey} variant="text" />
      )}
      {elementKey && !formatOnly && editor.onSetFieldHidden && (
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
          onClick={(e) => {
            stop(e);
            if (!editor.contentBusy) editor.onSetFieldHidden!(elementKey, !hidden);
          }}
          disabled={editor.contentBusy}
          title={hidden ? "Show on published page" : "Hide from published page"}
          data-testid={`button-field-toggle-${elementKey}`}
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      )}
      {elementKey && !formatOnly && editor.onDeleteField && (
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:pointer-events-none"
          onClick={(e) => {
            stop(e);
            if (!editor.contentBusy) editor.onDeleteField!(elementKey);
          }}
          disabled={editor.contentBusy}
          title="Delete element"
          data-testid={`button-field-delete-${elementKey}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function HiddenBadge() {
  return (
    <span
      contentEditable={false}
      className="absolute -top-2 left-1 z-30 inline-flex items-center gap-1 rounded bg-foreground/80 px-1.5 py-0.5 font-sans text-[10px] font-medium normal-case tracking-normal text-white shadow-sm"
    >
      <EyeOff className="h-2.5 w-2.5" />
      Hidden
    </span>
  );
}

/* ─────────────────────────────────────────────
   STYLE POPOVER
   The per-element / per-section formatting toolbar. `variant="text"` exposes
   font size / weight / alignment / color (for headings + text blocks);
   `variant="section"` exposes a background tone + vertical padding (for section
   wrappers). All writes go through editor.onSetElementStyle with sanitized,
   allow-listed values — never raw CSS. Span-based + contentEditable={false} so
   it's valid inside any inline text host, and every click stops propagation so
   it never triggers the host's click-to-edit.
───────────────────────────────────────────── */
/** Convert a computed CSS color (`rgb(...)`/`rgba(...)`) to a `#rrggbb` hex so it
 *  can seed an `<input type="color">`. Returns null for anything unparseable
 *  (e.g. a named/keyword color), letting the caller fall back to a default. */
function rgbToHex(rgb: string): string | null {
  const m = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  const h = (n: string) => Number(n).toString(16).padStart(2, "0");
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
}

/** Like rgbToHex but treats a fully-transparent color (alpha 0) as "no
 *  background" → returns null so a transparent host doesn't seed the picker with
 *  a misleading black. Used to seed the section Background color picker. */
function bgRgbToHex(rgb: string): string | null {
  const m = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/i);
  if (!m) return null;
  if (m[4] !== undefined && Number(m[4]) === 0) return null;
  const h = (n: string) => Number(n).toString(16).padStart(2, "0");
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
}

// Friendly labels for the curated theme-font keys shown in the Font picker.
const LP_CURATED_FONT_LABELS: Record<string, string> = {
  inter: "Inter",
  system: "System",
  poppins: "Poppins",
  montserrat: "Montserrat",
  georgia: "Georgia",
  playfair: "Playfair Display",
  merriweather: "Merriweather",
  mono: "Monospace",
};

/* ─────────────────────────────────────────────
   FONT PICKER (per text block)
   A searchable Font control for the text-block formatting popover. Offers, in
   order: "Default" (clears the override), the brand's own saved fonts, the
   curated theme fonts, and the full searchable Google Fonts catalog. Every
   option previews in ITS OWN typeface (loaded on demand into the popover's
   document, so it also previews correctly inside the mobile-preview iframe).
   The selected value is stored on the block's LpElementStyle.fontFamily as one
   of: a curated key | a Google family name | a "brand:<id>" token.
───────────────────────────────────────────── */
function LpFontControl({
  elementKey,
  value,
  onChange,
  onClear,
  stop,
  rootRef,
}: {
  elementKey: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  onClear: () => void;
  stop: (e: React.MouseEvent) => void;
  rootRef: React.RefObject<HTMLSpanElement | null>;
}) {
  const brandFonts = useLpBrandFonts();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const usableBrandFonts = brandFonts.filter(isUsableBrandFont);
  const doc = rootRef.current?.ownerDocument ?? (typeof document !== "undefined" ? document : null);

  // Resolve the current selection to a display label.
  const currentLabel = (() => {
    const v = value ?? null;
    if (!v) return "Default";
    if (v in LP_THEME_FONTS) return LP_CURATED_FONT_LABELS[v] ?? v;
    const brandId = /^brand:(.+)$/.exec(v)?.[1];
    if (brandId) {
      const f = usableBrandFonts.find((x) => x.id === brandId);
      return f ? f.family : "Default";
    }
    return v; // a Google family name
  })();

  // When the picker opens, batch-load the whole Google catalog into the popover's
  // document (one request) so every option previews in its own typeface.
  useEffect(() => {
    if (!open || !doc) return;
    ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((f) => f.family), doc);
    for (const f of usableBrandFonts) previewFamilyFor(f, doc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  type Opt = { value: string | null; label: string; preview: string; testId: string };
  const idFor = (v: string | null) =>
    (v ?? "default").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  const brandOpts: Opt[] = usableBrandFonts.map((f) => ({
    value: lpBrandFontToken(f.id),
    label: f.label ? `${f.family} · ${f.label}` : f.family,
    preview: doc ? previewFamilyFor(f, doc) : "inherit",
    testId: idFor(lpBrandFontToken(f.id)),
  }));
  const curatedOpts: Opt[] = LP_FONT_KEYS.map((k) => ({
    value: k,
    label: LP_CURATED_FONT_LABELS[k] ?? k,
    preview: LP_THEME_FONTS[k],
    testId: idFor(k),
  }));
  const googleOpts: Opt[] = GOOGLE_FONT_CATALOG.map((f) => ({
    value: f.family,
    label: f.family,
    preview: `"${f.family}", sans-serif`,
    testId: idFor(f.family),
  }));

  const q = query.trim().toLowerCase();
  const match = (o: Opt) => !q || o.label.toLowerCase().includes(q);
  const fBrand = brandOpts.filter(match);
  const fCurated = curatedOpts.filter(match);
  const fGoogle = googleOpts.filter(match);

  const choose = (v: string | null) => {
    if (v === null) onClear();
    else onChange(v);
    setOpen(false);
    setQuery("");
  };

  const optionBtn = (o: Opt, active: boolean) => (
    <button
      key={`${o.value ?? "default"}`}
      type="button"
      onClick={(e) => { stop(e); choose(o.value); }}
      className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs ${
        active ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
      }`}
      style={{ fontFamily: o.preview }}
      data-testid={`option-font-${o.testId}-${elementKey}`}
    >
      <span className="truncate">{o.label}</span>
      {active && <Check className="ml-auto h-3 w-3 shrink-0 text-primary" />}
    </button>
  );

  const groupLabel = (t: string) => (
    <span className="mt-1 block px-2 pb-0.5 pt-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{t}</span>
  );

  return (
    <span className="mb-2 block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Font</span>
      <button
        type="button"
        onClick={(e) => { stop(e); setOpen((o) => !o); }}
        className="flex h-7 w-full items-center justify-between gap-1 rounded border border-border px-2 text-xs outline-none hover:border-primary"
        data-testid={`button-font-${elementKey}`}
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <span className="mt-1 block rounded border border-border bg-white p-1">
          <input
            type="text"
            value={query}
            autoFocus
            placeholder="Search fonts…"
            onClick={stop}
            onChange={(e) => setQuery(e.target.value)}
            className="mb-1 h-7 w-full rounded border border-border px-2 text-xs outline-none focus:border-primary"
            data-testid={`input-font-search-${elementKey}`}
          />
          <span className="block max-h-44 overflow-auto">
            {!q && (
              <button
                type="button"
                onClick={(e) => { stop(e); choose(null); }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs ${
                  !value ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
                }`}
                data-testid={`button-font-default-${elementKey}`}
              >
                <span className="truncate">Default</span>
                {!value && <Check className="ml-auto h-3 w-3 shrink-0 text-primary" />}
              </button>
            )}
            {fBrand.length > 0 && groupLabel("Brand fonts")}
            {fBrand.map((o) => optionBtn(o, value === o.value))}
            {fCurated.length > 0 && groupLabel("Theme fonts")}
            {fCurated.map((o) => optionBtn(o, value === o.value))}
            {fGoogle.length > 0 && groupLabel("Google Fonts")}
            {fGoogle.map((o) => optionBtn(o, value === o.value))}
            {fBrand.length + fCurated.length + fGoogle.length === 0 && (
              <span className="block px-2 py-2 text-center text-[11px] text-muted-foreground">No fonts found</span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

export function LpStylePopover({
  elementKey,
  variant,
  className,
}: {
  elementKey: string;
  variant: "text" | "section";
  className?: string;
}) {
  const editor = useLpEditor();
  const style = useLpElementStyle(elementKey);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  // The element's CURRENT rendered color, read from the DOM when the popover
  // opens. Used to seed the color picker so it starts at what the element
  // actually shows today (not a hardcoded default) when no override is set yet.
  const [autoColor, setAutoColor] = useState<string | null>(null);
  // The section's CURRENT rendered background color, read from the DOM when the
  // popover opens. Seeds the Background picker so it starts at the section's real
  // background (not a hardcoded white) when no override is set yet.
  const [autoBg, setAutoBg] = useState<string | null>(null);
  if (!editor?.onSetElementStyle) return null;

  // Walk up from the toolbar to the styled host element and read its computed
  // color. `~=` matches the literal Tailwind group token (slash and all).
  const captureAutoColor = () => {
    const host = rootRef.current?.closest(
      '[class~="group/lpfield"], [class~="group/lpel"]',
    ) as HTMLElement | null;
    const src = host ?? rootRef.current?.parentElement?.parentElement ?? null;
    if (!src) return;
    setAutoColor(rgbToHex(getComputedStyle(src).color));
  };

  // Find the section's rendered background. The section toolbar lives in the
  // `group/lpsec` wrapper; the actual section content is the wrapper's last child
  // div (carries the section's inline style), and the real background sits on
  // that block or its first rendered element. Walk down the first-child chain a
  // few levels and take the first non-transparent background-color.
  const captureAutoBg = () => {
    const wrap = rootRef.current?.closest('[class~="group/lpsec"]') as HTMLElement | null;
    if (!wrap) return;
    const contentWrap = wrap.querySelector(":scope > div:last-child") as HTMLElement | null;
    let el: HTMLElement | null =
      (contentWrap?.firstElementChild as HTMLElement | null) ?? contentWrap;
    for (let depth = 0; el && depth < 5; depth++) {
      const hex = bgRgbToHex(getComputedStyle(el).backgroundColor);
      if (hex) {
        setAutoBg(hex);
        return;
      }
      el = el.firstElementChild as HTMLElement | null;
    }
  };

  const set = (patch: Partial<LpElementStyle>) => editor.onSetElementStyle!(elementKey, patch);
  const reset = () => editor.onResetElementStyle?.(elementKey);
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const hasOverride = !!style && Object.keys(style).length > 0;

  const segBtn = (active: boolean) =>
    `inline-flex h-6 min-w-6 items-center justify-center rounded px-1.5 text-xs ${
      active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
    } disabled:opacity-30`;

  return (
    <span ref={rootRef} contentEditable={false} className={`relative inline-flex ${className ?? ""}`}>
      <button
        type="button"
        className={`inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground ${
          hasOverride ? "text-primary" : ""
        }`}
        onClick={(e) => {
          stop(e);
          if (!open) {
            if (variant === "section") captureAutoBg();
            else captureAutoColor();
          }
          setOpen((o) => !o);
        }}
        title="Formatting"
        data-testid={`button-style-${elementKey}`}
      >
        <Type className="h-3 w-3" />
      </button>
      <FloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        open={open}
        placement="bottom"
        align="right"
        width={224}
        onClick={stop}
        className="block w-56 cursor-default rounded-lg border border-border bg-white p-2.5 text-left shadow-xl"
        testId={`popover-style-${elementKey}`}
      >
          {variant === "text" ? (
            <>
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Size</span>
                <span className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={10}
                    max={96}
                    value={style?.fontSize ?? ""}
                    placeholder="Auto"
                    onClick={stop}
                    onChange={(e) => {
                      const v = e.target.value;
                      set({ fontSize: v === "" ? (null as any) : Number(v) });
                    }}
                    className="h-7 w-20 rounded border border-border px-2 text-xs outline-none focus:border-primary"
                    data-testid={`input-fontsize-${elementKey}`}
                  />
                  <span className="text-[11px] text-muted-foreground">px</span>
                </span>
              </span>
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Weight</span>
                <span className="inline-flex gap-1">
                  <button type="button" className={segBtn(style?.fontWeight === "normal")} onClick={(e) => { stop(e); set({ fontWeight: style?.fontWeight === "normal" ? (null as any) : "normal" }); }} data-testid={`button-weight-normal-${elementKey}`}>Normal</button>
                  <button type="button" className={segBtn(style?.fontWeight === "bold")} onClick={(e) => { stop(e); set({ fontWeight: style?.fontWeight === "bold" ? (null as any) : "bold" }); }} data-testid={`button-weight-bold-${elementKey}`}><Bold className="h-3 w-3" /></button>
                </span>
              </span>
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Align</span>
                <span className="inline-flex gap-1">
                  {(["left", "center", "right"] as const).map((a) => (
                    <button key={a} type="button" className={segBtn(style?.align === a)} onClick={(e) => { stop(e); set({ align: style?.align === a ? (null as any) : a }); }} data-testid={`button-align-${a}-${elementKey}`}>
                      {a === "left" ? <AlignLeft className="h-3 w-3" /> : a === "center" ? <AlignCenter className="h-3 w-3" /> : <AlignRight className="h-3 w-3" />}
                    </button>
                  ))}
                </span>
              </span>
              <LpFontControl
                elementKey={elementKey}
                value={style?.fontFamily}
                onChange={(v) => set({ fontFamily: v })}
                onClear={() => set({ fontFamily: null as any })}
                stop={stop}
                rootRef={rootRef}
              />
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Color</span>
                <span className="flex items-center gap-1.5">
                  <input type="color" value={style?.color ?? autoColor ?? "#231f20"} onClick={stop} onChange={(e) => set({ color: e.target.value })} className="h-7 w-9 cursor-pointer rounded border border-border bg-white p-0.5" data-testid={`input-color-${elementKey}`} />
                  {style?.color && (
                    <button type="button" className="text-[11px] text-muted-foreground underline hover:text-foreground" onClick={(e) => { stop(e); set({ color: null as any }); }} data-testid={`button-color-clear-${elementKey}`}>Clear</button>
                  )}
                </span>
              </span>
            </>
          ) : (
            <>
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Background</span>
                <span className="flex items-center gap-1.5">
                  <input type="color" value={style?.background ?? autoBg ?? "#ffffff"} onClick={stop} onChange={(e) => set({ background: e.target.value })} className="h-7 w-9 cursor-pointer rounded border border-border bg-white p-0.5" data-testid={`input-bg-${elementKey}`} />
                  {style?.background && (
                    <button type="button" className="text-[11px] text-muted-foreground underline hover:text-foreground" onClick={(e) => { stop(e); set({ background: null as any }); }} data-testid={`button-bg-clear-${elementKey}`}>Clear</button>
                  )}
                </span>
              </span>
              <span className="mb-2 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Padding</span>
                <span className="inline-flex gap-1">
                  {LP_SPACING_SCALES.map((s) => (
                    <button key={s} type="button" className={segBtn(style?.paddingY === s)} onClick={(e) => { stop(e); set({ paddingY: style?.paddingY === s ? (null as any) : s }); }} data-testid={`button-padding-${s}-${elementKey}`}>
                      {s === "compact" ? "S" : s === "default" ? "M" : "L"}
                    </button>
                  ))}
                </span>
              </span>
            </>
          )}
          <span className="mt-1 flex items-center justify-between border-t border-border pt-2">
            <button type="button" className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-30" onClick={(e) => { stop(e); reset(); }} disabled={!hasOverride} data-testid={`button-style-reset-${elementKey}`}>
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
            <button type="button" className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90" onClick={(e) => { stop(e); setOpen(false); }} data-testid={`button-style-done-${elementKey}`}>
              <Check className="h-3 w-3" /> Done
            </button>
          </span>
      </FloatingPanel>
    </span>
  );
}

/* ─────────────────────────────────────────────
   EDITABLE ELEMENT
   Block-level wrapper for a single non-text component (an image, a video, or a
   block of markup) keyed by a stable string. In the editor it adds a hover
   toolbar (hide/show + delete) and dims hidden elements; on the published page
   it returns null for hidden/deleted elements and renders children directly
   otherwise. Mirrors EditableItem but keyed by an arbitrary string instead of
   an array path + index.
───────────────────────────────────────────── */
export function EditableElement({
  elementKey,
  children,
  className,
  testId,
  as: As = "div",
  toolbarSide = "right",
  regenBlockKey,
}: {
  elementKey: string;
  children: React.ReactNode;
  className?: string;
  testId?: string;
  as?: keyof JSX.IntrinsicElements;
  /** Which top corner the hover toolbar sits in. Images use "left" so the
   *  toolbar clears the top-right Regenerate button / live-data dot. */
  toolbarSide?: "left" | "right";
  /** Optional copy-block key: when set (and the editor exposes
   *  onRegenerateBlock), the hover toolbar also offers a compact Regenerate
   *  control that re-rolls this element's copy block — used by blob templates
   *  (Temecula Passport, …) whose sections aren't registry section types and
   *  so can't render <LpSectionRegenerate>. */
  regenBlockKey?: string;
}) {
  const editor = useLpEditor();
  const st = useLpElementState(elementKey);
  const canEdit = !!editor && !!(editor.onSetFieldHidden || editor.onDeleteField);

  // Reorder-group membership: when this element is rendered inside an
  // <LpReorderGroup> and the editor exposes onMoveElement, surface up/down arrows
  // that reorder it ONLY within its sibling stack (never across the page).
  const group = useContext(LpReorderGroupContext);
  const groupIdx = group ? group.orderedKeys.indexOf(elementKey) : -1;
  const canReorder = !!editor?.onMoveElement && group != null && groupIdx >= 0;
  const isFirst = groupIdx === 0;
  const isLast = group != null && groupIdx === group.orderedKeys.length - 1;
  const moveWithin = (dir: "up" | "down") => {
    if (!group || !editor?.onMoveElement) return;
    const keys = [...group.orderedKeys];
    const i = keys.indexOf(elementKey);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    editor.onMoveElement(group.groupKey, keys);
  };

  if (st.deleted) return null;
  if (!canEdit) {
    if (st.hidden) return null;
    if (As !== "div") return <As className={className}>{children}</As>;
    return <>{children}</>;
  }

  return (
    <As
      className={`relative group/lpel ${st.hidden ? "opacity-40 grayscale" : ""} ${className ?? ""}`}
      data-testid={testId}
    >
      <div className={`absolute ${toolbarSide === "left" ? "left-1" : "right-1"} top-1 z-30 flex items-center gap-0.5 rounded-md border border-border bg-white/95 p-0.5 shadow-md opacity-0 transition-opacity group-hover/lpel:opacity-100 focus-within:opacity-100 backdrop-blur-sm`}>
        {regenBlockKey && <LpBlockRegenerate blockKey={regenBlockKey} compact />}
        {canReorder && (
          <>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!editor!.contentBusy) moveWithin("up");
              }}
              disabled={editor!.contentBusy || isFirst}
              title="Move up"
              data-testid={`button-element-move-up-${elementKey}`}
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!editor!.contentBusy) moveWithin("down");
              }}
              disabled={editor!.contentBusy || isLast}
              title="Move down"
              data-testid={`button-element-move-down-${elementKey}`}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        {editor!.onSetFieldHidden && (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!editor!.contentBusy) editor!.onSetFieldHidden!(elementKey, !st.hidden);
            }}
            disabled={editor!.contentBusy}
            title={st.hidden ? "Show on published page" : "Hide from published page"}
            data-testid={`button-element-toggle-${elementKey}`}
          >
            {st.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        )}
        {editor!.onDeleteField && (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:pointer-events-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!editor!.contentBusy) editor!.onDeleteField!(elementKey);
            }}
            disabled={editor!.contentBusy}
            title="Delete element"
            data-testid={`button-element-delete-${elementKey}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {st.hidden && (
        <div className={`absolute ${toolbarSide === "left" ? "right-1" : "left-1"} top-1 z-30 flex items-center gap-1 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm`}>
          <EyeOff className="h-2.5 w-2.5" />
          Hidden
        </div>
      )}
      <LpComponentFormatOnlyContext.Provider value={true}>
        {children}
      </LpComponentFormatOnlyContext.Provider>
    </As>
  );
}

/* ─────────────────────────────────────────────
   VIDEO URL EDITOR
   Editor-only overlay to paste/replace a per-page video URL, or reset it back
   to the default (brand video → hardcoded fallback). Degrades to nothing on the
   published page (no editor in context). Stored on the page's generatedContent
   via onSetVideoUrl — never on the brand record.
───────────────────────────────────────────── */
export function VideoUrlEditor({
  slot,
  currentOverride,
}: {
  slot: "making" | "lifestyle" | "coffee-shop-reel";
  /** The current per-page override URL (empty/undefined when none set). */
  currentOverride?: string | null;
}) {
  const editor = useLpEditor();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(currentOverride ?? "");
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Keep the input in sync when the persisted override changes underneath us.
  useEffect(() => {
    if (!open) setVal(currentOverride ?? "");
  }, [currentOverride, open]);

  if (!editor?.onSetVideoUrl) return null;

  const save = () => {
    editor.onSetVideoUrl!(slot, val.trim() ? val.trim() : null);
    setOpen(false);
  };
  const onPickVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor.onUploadMedia) return;
    setUploading(true);
    try {
      await editor.onUploadMedia({ kind: "video", slot }, file);
    } finally {
      setUploading(false);
    }
  };
  const clear = () => {
    editor.onSetVideoUrl!(slot, null);
    setVal("");
    setOpen(false);
  };

  return (
    <div className="absolute right-2 top-2 z-40" data-testid={`video-url-editor-${slot}`}>
      <input
        ref={fileRef}
        type="file"
        accept={LP_VIDEO_ACCEPT}
        className="hidden"
        onChange={onPickVideo}
        data-testid={`input-upload-video-${slot}`}
      />
      {!open ? (
        <div className="flex items-center gap-1">
          {editor.onUploadMedia && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!uploading && !editor.contentBusy) fileRef.current?.click();
              }}
              disabled={uploading || editor.contentBusy}
              title="Upload your own video"
              data-testid={`button-upload-video-${slot}`}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
            title="Edit video URL"
            data-testid={`button-edit-video-${slot}`}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted"
          >
            <Pencil className="h-3.5 w-3.5" />
            {currentOverride ? "Edit URL" : "Set URL"}
          </button>
        </div>
      ) : (
        <div
          className="w-64 rounded-lg border border-border bg-white p-2 font-sans shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="mb-1 text-[11px] font-semibold text-foreground/80">Video URL (this page only)</p>
          <input
            type="text"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="https://…/video.mp4"
            data-testid={`input-video-url-${slot}`}
            className="mb-2 w-full rounded border border-border px-2 py-1 text-xs outline-none focus:border-primary"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
              }
            }}
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={save}
              disabled={editor.contentBusy}
              data-testid={`button-save-video-${slot}`}
              className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
            >
              <Check className="h-3 w-3" /> Save
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={editor.contentBusy}
              data-testid={`button-reset-video-${slot}`}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
              title="Reset to default video"
            >
              <RotateCcw className="h-3 w-3" /> Default
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`button-cancel-video-${slot}`}
              className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted"
              title="Cancel"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   EDITABLE ITEM
   Wraps one repeatable item (a FAQ entry, review, card, timeline step…). In the
   editor it adds a small hover toolbar (hide/show + delete) and dims hidden
   items with a badge. On the public page it renders children directly (no extra
   wrapper) and returns null for hidden items.
───────────────────────────────────────────── */
export function EditableItem({
  path,
  index,
  count,
  hidden,
  children,
  allowDelete = true,
  className,
  testId,
  toolbarSide = "right",
}: {
  path: LpContentPath;
  index: number;
  /** Total number of sibling items in the array. When provided (and > 1) and the
   *  editor exposes `onItemMove`, up/down reorder arrows appear (disabled at the
   *  ends). */
  count?: number;
  hidden?: boolean;
  children: React.ReactNode;
  allowDelete?: boolean;
  className?: string;
  testId?: string;
  /** Which top corner the hover toolbar sits in. Image-bearing items use
   *  "left" so the toolbar clears the top-right Regenerate button. */
  toolbarSide?: "left" | "right";
}) {
  const editor = useLpEditor();
  const canEdit = !!(editor?.onItemSetHidden || editor?.onItemDelete || editor?.onItemMove);
  const canReorder = !!editor?.onItemMove && typeof count === "number" && count > 1;

  if (!canEdit) {
    if (hidden) return null;
    return <>{children}</>;
  }

  return (
    <div
      className={`relative group/lpitem ${hidden ? "opacity-40 grayscale" : ""} ${className ?? ""}`}
      data-testid={testId}
    >
      <div className={`absolute ${toolbarSide === "left" ? "left-1" : "right-1"} top-1 z-30 flex items-center gap-0.5 rounded-md border border-border bg-white/95 p-0.5 shadow-md opacity-0 transition-opacity group-hover/lpitem:opacity-100 backdrop-blur-sm`}>
        {canReorder && (
          <>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
              onClick={() => { if (!editor!.contentBusy) editor!.onItemMove!(path, index, "up"); }}
              disabled={editor!.contentBusy || index <= 0}
              title="Move up"
              data-testid={testId ? `button-item-move-up-${testId}` : undefined}
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
              onClick={() => { if (!editor!.contentBusy) editor!.onItemMove!(path, index, "down"); }}
              disabled={editor!.contentBusy || index >= (count as number) - 1}
              title="Move down"
              data-testid={testId ? `button-item-move-down-${testId}` : undefined}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
          onClick={() => editor!.onItemSetHidden?.(path, index, !hidden)}
          disabled={editor!.contentBusy || !editor!.onItemSetHidden}
          title={hidden ? "Show on published page" : "Hide from published page"}
          data-testid={testId ? `button-item-toggle-${testId}` : undefined}
        >
          {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
        {allowDelete && (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:pointer-events-none"
            onClick={() => editor!.onItemDelete?.(path, index)}
            disabled={editor!.contentBusy || !editor!.onItemDelete}
            title="Delete item"
            data-testid={testId ? `button-item-delete-${testId}` : undefined}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {hidden && (
        <div className={`absolute ${toolbarSide === "left" ? "right-1" : "left-1"} top-1 z-30 flex items-center gap-1 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm`}>
          <EyeOff className="h-2.5 w-2.5" />
          Hidden
        </div>
      )}
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   REGENERATE IMAGE BUTTON
   A small overlay button (hover-toolbar style) shown over an AI image slot when
   the editor is active. Reuses the existing single-slot regeneration handler;
   the per-slot status poll drives the spinner once clicked.
───────────────────────────────────────────── */
export function RegenerateButton({
  onClick,
  className,
  testId,
}: {
  onClick: () => void;
  className?: string;
  testId?: string;
}) {
  const editor = useLpEditor();
  if (!editor) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!editor.contentBusy) onClick();
      }}
      disabled={editor.contentBusy}
      title="Regenerate image"
      data-testid={testId}
      className={`absolute right-2 top-2 z-30 inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md opacity-0 transition-opacity group-hover/lpimg:opacity-100 backdrop-blur-sm hover:bg-muted disabled:opacity-40 ${className ?? ""}`}
    >
      <RotateCw className="h-3.5 w-3.5" />
      Regenerate
    </button>
  );
}

/* ─────────────────────────────────────────────
   REGENERATE A COPY BLOCK (INLINE)
   A tiny hover-toolbar control rendered next to each editable text field via
   FieldToolbar. Opens a popover with an optional feedback field and a
   "Regenerate" button that re-runs just this field's copy block (workflow).
   Wires to editor.onRegenerateBlock. Renders nothing on the public page or for
   an unknown block key.
───────────────────────────────────────────── */
function LpBlockRegenerate({
  blockKey,
  compact = false,
}: {
  blockKey: string;
  /** Icon-only rendering for the EditableElement hover toolbar (sits alongside
   *  the move/hide/delete icons instead of inline next to a text field). */
  compact?: boolean;
}) {
  const editor = useLpEditor();
  const regenHighlight = useLpRegenHighlight();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Clear the cross-field highlight when this control unmounts.
  useEffect(() => () => regenHighlight.setBlock(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss the popover on a click outside the trigger/panel, or as soon as the
  // page scrolls (otherwise the fixed-position panel just follows the anchor and
  // lingers on screen). Either way also clears the cross-field highlight.
  useEffect(() => {
    if (!open) return;
    const doc = rootRef.current?.ownerDocument ?? document;
    const win = doc.defaultView ?? window;
    const close = () => {
      setOpen(false);
      regenHighlight.setBlock(null);
    };
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    doc.addEventListener("pointerdown", onDown, true);
    win.addEventListener("scroll", close, true);
    return () => {
      doc.removeEventListener("pointerdown", onDown, true);
      win.removeEventListener("scroll", close, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor?.onRegenerateBlock) return null;
  const label = lpCopyBlockLabel(blockKey);
  if (!label) return null;
  const regenerating = !!editor.isBlockRegenerating?.(blockKey);
  const busy = !!editor.contentBusy || regenerating;

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  // Outline every field this block rewrites while the control is hovered/open,
  // scoped to the control's own visual <section> so reused blocks (villain /
  // quotes) don't light up matching copy in far-away sections.
  const highlightOn = () =>
    regenHighlight.setBlock(blockKey, rootRef.current?.closest("section") ?? null);
  const highlightOff = () => {
    if (!open) regenHighlight.setBlock(null);
  };
  const closePanel = () => {
    setOpen(false);
    regenHighlight.setBlock(null);
  };

  return (
    <span
      ref={rootRef}
      contentEditable={false}
      className="relative inline-flex"
      onMouseEnter={highlightOn}
      onMouseLeave={highlightOff}
    >
      <button
        type="button"
        disabled={regenerating}
        className={
          compact
            ? "inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
            : "inline-flex h-5 items-center justify-center gap-1 rounded px-1.5 font-sans text-[11px] font-semibold normal-case tracking-normal text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
        }
        onClick={(e) => {
          stop(e);
           const next = !open;
           setOpen(next);
           if (next) highlightOn();
           else regenHighlight.setBlock(null);
        }}
        onFocus={highlightOn}
        title={compact ? "Regenerate section" : "Regenerate"}
        data-testid={`button-regenerate-block-${blockKey}`}
      >
        {regenerating ? (
          <Loader2 className={compact ? "h-3.5 w-3.5 animate-spin" : "h-3 w-3 animate-spin"} />
        ) : (
          <RotateCw className={compact ? "h-3.5 w-3.5" : "h-3 w-3"} />
        )}
        {!compact && (regenerating ? "Regenerating…" : "Regenerate")}
      </button>
      <FloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="right"
        width={264}
        onClick={stop}
        className="block w-66 cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
        testId={`popover-regenerate-block-${blockKey}`}
      >
        <div className="mb-1 text-xs font-semibold text-foreground">Regenerate</div>
        <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
          Rewrites all of the highlighted copy together — it's written as one
          set, so it updates as a set.
        </p>
        <textarea
          rows={3}
          value={feedback}
          onClick={stop}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Optional feedback (e.g. make it punchier)"
          className="block w-full resize-none rounded border border-border px-2 py-1.5 text-xs outline-none focus:border-primary"
          data-testid={`input-feedback-block-${blockKey}`}
        />
        <button
          type="button"
          disabled={busy}
          onClick={(e) => {
            stop(e);
            editor.onRegenerateBlock?.(blockKey, feedback.trim());
            closePanel();
          }}
          className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid={`button-regenerate-block-submit-${blockKey}`}
        >
          {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          {regenerating ? "Regenerating…" : "Regenerate"}
        </button>
      </FloatingPanel>
    </span>
  );
}

/* ─────────────────────────────────────────────
   REGENERATE SECTION WITH FEEDBACK
   A section-toolbar control that opens a panel with a "Regenerate section"
   control: shared optional feedback fanned to every copy workflow in the
   section, plus a scope choice (Text / Text and images) that optionally
   re-renders the section's AI images. Per-block regeneration lives inline on
   each text field (see LpBlockRegenerate). Wires to editor.onRegenerateSection.
   Renders nothing on the public page (no editor) or for sections with no
   regenerable copy blocks.
───────────────────────────────────────────── */
export function LpSectionRegenerate({ type }: { type: LpSectionType }) {
  const editor = useLpEditor();
  const [open, setOpen] = useState(false);
  const [sectionFeedback, setSectionFeedback] = useState("");
  const [scope, setScope] = useState<"text" | "images" | "both">("text");
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Dismiss the popover on a click outside the trigger/panel, or as soon as the
  // page scrolls (else the fixed-position panel just follows the anchor).
  useEffect(() => {
    if (!open) return;
    const doc = rootRef.current?.ownerDocument ?? document;
    const win = doc.defaultView ?? window;
    const close = () => setOpen(false);
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    doc.addEventListener("pointerdown", onDown, true);
    win.addEventListener("scroll", close, true);
    return () => {
      doc.removeEventListener("pointerdown", onDown, true);
      win.removeEventListener("scroll", close, true);
    };
  }, [open]);

  if (!editor?.onRegenerateBlock && !editor?.onRegenerateSection) return null;
  const blocks = lpSectionCopyBlocks(type);
  if (blocks.length === 0) return null;
  const hasImages = lpSectionHasImages(type);
  const regenerating = blocks.some((b) => editor.isBlockRegenerating?.(b.key));
  const busy = !!editor.contentBusy || regenerating;

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const segBtn = (active: boolean) =>
    `inline-flex h-6 items-center justify-center rounded px-2 text-xs ${
      active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
    } disabled:opacity-30`;

  return (
    <span ref={rootRef} contentEditable={false} className="relative inline-flex">
      <button
        type="button"
        disabled={regenerating}
        className="inline-flex h-7 items-center justify-center gap-1 rounded-md px-2 font-sans text-xs font-medium normal-case tracking-normal text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
        onClick={(e) => { stop(e); setOpen((o) => !o); }}
        title="Regenerate"
        data-testid={`button-regenerate-section-${type}`}
      >
        {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
        {regenerating ? "Regenerating…" : "Regenerate"}
      </button>
      <FloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="right"
        width={288}
        onClick={stop}
        className="block w-72 cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
        testId={`popover-regenerate-${type}`}
      >
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Regenerate section
        </div>
        <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
          To regenerate just one block, hover that text and use its Regenerate
          control.
        </p>
        <textarea
          rows={3}
          value={sectionFeedback}
          onClick={stop}
          onChange={(e) => setSectionFeedback(e.target.value)}
          placeholder="Optional feedback"
          className="block w-full resize-none rounded border border-border px-2 py-1.5 text-xs outline-none focus:border-primary"
          data-testid={`input-feedback-section-${type}`}
        />
        {hasImages && (
          <div className="mt-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Regenerate</div>
            <div className="inline-flex gap-1">
              <button
                type="button"
                className={segBtn(scope === "text")}
                onClick={(e) => { stop(e); setScope("text"); }}
                data-testid={`button-scope-text-${type}`}
              >
                Text
              </button>
              <button
                type="button"
                className={segBtn(scope === "images")}
                onClick={(e) => { stop(e); setScope("images"); }}
                data-testid={`button-scope-images-${type}`}
              >
                Images
              </button>
              <button
                type="button"
                className={segBtn(scope === "both")}
                onClick={(e) => { stop(e); setScope("both"); }}
                data-testid={`button-scope-both-${type}`}
              >
                Both
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={(e) => {
            stop(e);
            editor.onRegenerateSection?.(type, sectionFeedback.trim(), hasImages ? scope : "text");
          }}
          className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid={`button-regenerate-section-submit-${type}`}
        >
          {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          {regenerating ? "Regenerating…" : "Regenerate"}
        </button>
      </FloatingPanel>
    </span>
  );
}

/* ─────────────────────────────────────────────
   MEDIA UPLOAD VALIDATION + ACTIONS
   Client-side pre-checks (instant feedback before the upload round-trip). The
   server re-validates and is the source of truth, but these keep an obviously
   wrong file (wrong type / too big) from being uploaded at all.
───────────────────────────────────────────── */
export const LP_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp";
export const LP_VIDEO_ACCEPT = ".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime";
const LP_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const LP_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
const LP_IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp)$/i;
const LP_VIDEO_EXT = /\.(mp4|webm|mov)$/i;

/** Returns a specific error message for a wrong-type/oversized file, or null
 *  when the file passes the client-side pre-check for the given kind. */
export function validateLpMediaFile(file: File, kind: "image" | "video"): string | null {
  if (kind === "image") {
    const ok = LP_IMAGE_EXT.test(file.name) || file.type.startsWith("image/");
    if (!ok) return "That slot takes an image. Please choose a JPG, PNG, GIF, or WebP file.";
    if (file.size > LP_IMAGE_MAX_BYTES) return "Image is too large (max 10MB).";
  } else {
    const ok = LP_VIDEO_EXT.test(file.name) || file.type.startsWith("video/");
    if (!ok) return "That slot takes a video. Please choose an MP4, WebM, or MOV file.";
    if (file.size > LP_VIDEO_MAX_BYTES) return "Video is too large (max 50MB).";
  }
  return null;
}

/* Image-slot action overlay: an Upload button on EVERY image slot, plus a
   Regenerate button ONLY on AI (FAL-generated) slots that haven't been replaced
   by an upload, plus a "Use AI image" revert button on uploaded slots. Renders
   nothing on the public page (no editor). */
export function LpImageActions({
  slotKey,
  canRegenerate = false,
  onRegenerate,
  className,
  testIdPrefix,
  productPhotos,
  revertLabel = "Use AI",
  revertTitle = "Revert to the AI-generated image",
  popoverPlacement = "bottom",
}: {
  /** Stable slot key, also used as the media-override key (e.g. "hero:0"). */
  slotKey: string;
  /** True when this is an AI slot that supports regeneration. */
  canRegenerate?: boolean;
  /** Regenerate handler (only used when canRegenerate and not overridden). */
  onRegenerate?: () => void;
  className?: string;
  testIdPrefix?: string;
  /** When provided (and non-empty), shows a "Choose from product photos" picker
   *  that lists these URLs; selecting one points the slot at that photo via the
   *  media-override system. Used on slots whose default is a product gallery
   *  photo (closing CTA card, bundle/order-summary, sticky bottom bar). */
  productPhotos?: string[];
  /** Revert button label/title. Defaults to the AI-image wording; product-photo
   *  slots pass "Use default" since their default is the product gallery photo. */
  revertLabel?: string;
  revertTitle?: string;
  /** Which way the "Choose from product photos" popover opens. Use "top" for
   *  slots pinned to the bottom of the viewport (e.g. the sticky bottom bar). */
  popoverPlacement?: "top" | "bottom";
}) {
  const editor = useLpEditor();
  const overrideUrl = useLpMediaOverride(slotKey);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [chooseOpen, setChooseOpen] = useState(false);
  const chooseRef = useRef<HTMLDivElement>(null);
  const choosePanelRef = useRef<HTMLDivElement>(null);

  // Close the photo picker on an outside click / Escape. The panel is portaled
  // out of chooseRef's subtree, so treat clicks inside it as "inside" too.
  useEffect(() => {
    if (!chooseOpen) return;
    const onDocPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      const inAnchor = chooseRef.current?.contains(target);
      const inPanel = choosePanelRef.current?.contains(target);
      if (!inAnchor && !inPanel) {
        setChooseOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setChooseOpen(false);
    };
    // Listen on the document that owns the trigger so outside-click / Escape work
    // inside the mobile-preview iframe (events don't cross document boundaries).
    const doc = chooseRef.current?.ownerDocument ?? document;
    doc.addEventListener("pointerdown", onDocPointer, true);
    doc.addEventListener("keydown", onKey);
    return () => {
      doc.removeEventListener("pointerdown", onDocPointer, true);
      doc.removeEventListener("keydown", onKey);
    };
  }, [chooseOpen]);

  if (!editor) return null;
  const canUpload = !!editor.onUploadMedia;
  const isUploaded = !!overrideUrl;
  const showRegen = canRegenerate && !isUploaded && !!onRegenerate;
  const canRevert = isUploaded && !!editor.onClearMedia;
  const photos = Array.isArray(productPhotos)
    ? productPhotos.filter((u): u is string => typeof u === "string" && u.trim().length > 0)
    : [];
  const canChoose = photos.length > 0 && !!editor.onChooseMedia;
  if (!canUpload && !showRegen && !canRevert && !canChoose) return null;

  const idbase = testIdPrefix ?? slotKey.replace(/[^a-zA-Z0-9_-]/g, "-");
  const busy = !!editor.contentBusy || uploading;

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor.onUploadMedia) return;
    setUploading(true);
    try {
      await editor.onUploadMedia({ kind: "image", key: slotKey }, file);
    } finally {
      setUploading(false);
    }
  };

  const onChoosePhoto = (url: string) => {
    setChooseOpen(false);
    if (busy) return;
    editor.onChooseMedia?.(slotKey, url);
  };

  return (
    <div
      className={`absolute right-2 top-2 z-30 flex items-center gap-1 opacity-0 transition-opacity group-hover/lpimg:opacity-100 ${className ?? ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={LP_IMAGE_ACCEPT}
        className="hidden"
        onChange={onPick}
        data-testid={`input-upload-${idbase}`}
      />
      {canUpload && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) inputRef.current?.click();
          }}
          disabled={busy}
          title={isUploaded ? "Replace uploaded image" : "Upload your own image"}
          data-testid={`button-upload-${idbase}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {isUploaded ? "Replace" : "Upload"}
        </button>
      )}
      {canChoose && (
        <div className="relative" ref={chooseRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!busy) setChooseOpen((o) => !o);
            }}
            disabled={busy}
            title="Choose from product photos"
            data-testid={`button-choose-photo-${idbase}`}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
          >
            <Images className="h-3.5 w-3.5" />
            Choose
          </button>
          <FloatingPanel
            anchorRef={chooseRef as React.RefObject<HTMLElement | null>}
            panelRef={choosePanelRef}
            open={chooseOpen}
            placement={popoverPlacement}
            align="right"
            width={256}
            className="w-64 rounded-lg border border-border bg-white p-2 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            testId={`popover-choose-photo-${idbase}`}
          >
              <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-foreground/80">
                Product photos
              </p>
              <div className="grid max-h-60 grid-cols-3 gap-1.5 overflow-y-auto">
                {photos.map((url, i) => {
                  const selected = overrideUrl === url;
                  return (
                    <button
                      key={`${i}-${url}`}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChoosePhoto(url);
                      }}
                      disabled={busy}
                      title="Use this product photo"
                      data-testid={`button-choose-photo-option-${idbase}-${i}`}
                      className={`relative aspect-square overflow-hidden rounded-md border ${
                        selected ? "border-primary ring-2 ring-primary" : "border-border"
                      } bg-muted hover:border-primary disabled:opacity-40`}
                    >
                      <img
                        src={url}
                        alt={`Product photo ${i + 1}`}
                        className="h-full w-full object-cover"
                      />
                      {selected && (
                        <span className="absolute right-0.5 top-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
          </FloatingPanel>
        </div>
      )}
      {showRegen && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) onRegenerate?.();
          }}
          disabled={busy}
          title="Regenerate image"
          data-testid={`button-regenerate-${idbase}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
        >
          <RotateCw className="h-3.5 w-3.5" />
          Regenerate
        </button>
      )}
      {canRevert && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) editor.onClearMedia?.(slotKey);
          }}
          disabled={busy}
          title={revertTitle}
          data-testid={`button-revert-${idbase}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {revertLabel}
        </button>
      )}
    </div>
  );
}

/* Review-slot action overlay: a "Choose" button that opens a picker listing
   every real product review so the user can feature a specific one (mirrors the
   product-photo "Choose" picker), plus a "Use default" revert button once a
   review has been picked. Renders nothing on the public page (no editor) or when
   there are no real reviews to choose from. */
export function LpReviewActions({
  slotKey,
  reviews,
  className,
  testIdPrefix,
  popoverPlacement = "bottom",
}: {
  /** Stable slot key, also used as the review-override key (e.g. "hero"). */
  slotKey: string;
  /** Real product reviews available to feature. Empty = nothing to pick. */
  reviews: LpReviewChoice[];
  className?: string;
  testIdPrefix?: string;
  popoverPlacement?: "top" | "bottom";
}) {
  const editor = useLpEditor();
  const override = useLpReviewOverride(slotKey);
  const [chooseOpen, setChooseOpen] = useState(false);
  const chooseRef = useRef<HTMLDivElement>(null);
  const choosePanelRef = useRef<HTMLDivElement>(null);

  // Close the picker on an outside click / Escape. The panel is portaled out of
  // chooseRef's subtree, so treat clicks inside it as "inside" too.
  useEffect(() => {
    if (!chooseOpen) return;
    const onDocPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      const inAnchor = chooseRef.current?.contains(target);
      const inPanel = choosePanelRef.current?.contains(target);
      if (!inAnchor && !inPanel) setChooseOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setChooseOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [chooseOpen]);

  if (!editor) return null;
  const options = Array.isArray(reviews)
    ? reviews.filter(
        (r): r is LpReviewChoice =>
          !!r && typeof r.reviewText === "string" && r.reviewText.trim().length > 0,
      )
    : [];
  const canChoose = options.length > 0 && !!editor.onChooseReview;
  const canRevert = !!override && !!editor.onClearReview;
  if (!canChoose && !canRevert) return null;

  const idbase = testIdPrefix ?? slotKey.replace(/[^a-zA-Z0-9_-]/g, "-");
  const busy = !!editor.contentBusy;

  const isSelected = (r: LpReviewChoice) =>
    !!override &&
    override.reviewText === r.reviewText &&
    (override.reviewer ?? null) === (r.reviewer ?? null);

  const onChoose = (r: LpReviewChoice) => {
    setChooseOpen(false);
    if (busy) return;
    editor.onChooseReview?.(slotKey, r);
  };

  return (
    <div
      className={`absolute right-2 top-2 z-30 flex items-center gap-1 opacity-0 transition-opacity group-hover/lpreview:opacity-100 ${className ?? ""}`}
    >
      {canChoose && (
        <div className="relative" ref={chooseRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!busy) setChooseOpen((o) => !o);
            }}
            disabled={busy}
            title="Choose which review to feature"
            data-testid={`button-choose-review-${idbase}`}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
          >
            <MessageSquareQuote className="h-3.5 w-3.5" />
            Choose
          </button>
          <FloatingPanel
            anchorRef={chooseRef as React.RefObject<HTMLElement | null>}
            panelRef={choosePanelRef}
            open={chooseOpen}
            placement={popoverPlacement}
            align="right"
            width={288}
            className="w-72 rounded-lg border border-border bg-white p-2 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            testId={`popover-choose-review-${idbase}`}
          >
            <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-foreground/80">
              Featured review
            </p>
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {options.map((r, i) => {
                const selected = isSelected(r);
                return (
                  <button
                    key={`${i}-${r.reviewText.slice(0, 24)}`}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChoose(r);
                    }}
                    disabled={busy}
                    title="Feature this review"
                    data-testid={`button-choose-review-option-${idbase}-${i}`}
                    className={`flex w-full flex-col gap-0.5 rounded-md border p-2 text-left ${
                      selected ? "border-primary ring-2 ring-primary" : "border-border"
                    } bg-white hover:border-primary disabled:opacity-40`}
                  >
                    <span className="flex items-center gap-1">
                      <span className="inline-flex items-center text-[#F5A623]">
                        {Array.from({ length: r.rating }).map((_, s) => (
                          <Star key={s} className="h-3 w-3 fill-[#F5A623]" />
                        ))}
                      </span>
                      {r.reviewer && (
                        <span className="truncate text-[10px] font-semibold text-foreground/70">
                          {r.reviewer}
                        </span>
                      )}
                      {selected && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
                    </span>
                    <span className="line-clamp-3 text-[11px] leading-snug text-foreground/80">
                      {r.reviewText}
                    </span>
                  </button>
                );
              })}
            </div>
          </FloatingPanel>
        </div>
      )}
      {canRevert && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) editor.onClearReview?.(slotKey);
          }}
          disabled={busy}
          title="Revert to the default first review"
          data-testid={`button-revert-review-${idbase}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white/95 px-2 py-1 font-sans text-xs font-semibold text-foreground shadow-md backdrop-blur-sm hover:bg-muted disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Use default
        </button>
      )}
    </div>
  );
}
